import styles from "../../Styles/Knowledge Center/AddEntry.module.css";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Toast from "../../Components/Toast";
import Cookies from "js-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, 
  faArrowRightFromBracket 
} from "@fortawesome/free-solid-svg-icons";

export const AddEntry = () => {
  const [title, setTitle] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [beginnerBody, setBeginnerBody] = useState("");
  const [moderateBody, setModerateBody] = useState("");
  const [advancedBody, setAdvancedBody] = useState("");
  const [beginnerFooter, setBeginnerFooter] = useState("");
  const [moderateFooter, setModerateFooter] = useState("");
  const [advancedFooter, setAdvancedFooter] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState("");
  const [topicName, setTopicName] = useState("");

  const beginnerBodyEditorRef = useRef(null);
  const moderateBodyEditorRef = useRef(null);
  const advancedBodyEditorRef = useRef(null);
  const beginnerFooterEditorRef = useRef(null);
  const moderateFooterEditorRef = useRef(null);
  const advancedFooterEditorRef = useRef(null);

  const { mainCategoryId, subCategoryId, topicId, entryId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTopicName = async () => {
      try {
        if (!mainCategoryId || !subCategoryId || !topicId) {
          return;
        }

        const token = Cookies.get("adminToken");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/admin/entries/${mainCategoryId}/${subCategoryId}/${topicId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        const data = response.data?.data;
        if (data?.topic?.name) {
          setTopicName(data.topic.name);
        }
      } catch (error) {
        console.error("topic fetching error", error);
      }
    };

    fetchTopicName();
  }, [mainCategoryId, subCategoryId, topicId]);

  const readEditorValues = () => {
    const currentBeginnerBody =
      beginnerBodyEditorRef.current?.innerHTML != null
        ? beginnerBodyEditorRef.current.innerHTML
        : beginnerBody;
    const currentModerateBody =
      moderateBodyEditorRef.current?.innerHTML != null
        ? moderateBodyEditorRef.current.innerHTML
        : moderateBody;
    const currentAdvancedBody =
      advancedBodyEditorRef.current?.innerHTML != null
        ? advancedBodyEditorRef.current.innerHTML
        : advancedBody;

    const currentBeginnerFooter =
      beginnerFooterEditorRef.current?.innerHTML != null
        ? beginnerFooterEditorRef.current.innerHTML
        : beginnerFooter;
    const currentModerateFooter =
      moderateFooterEditorRef.current?.innerHTML != null
        ? moderateFooterEditorRef.current.innerHTML
        : moderateFooter;
    const currentAdvancedFooter =
      advancedFooterEditorRef.current?.innerHTML != null
        ? advancedFooterEditorRef.current.innerHTML
        : advancedFooter;

    return {
      currentBeginnerBody,
      currentModerateBody,
      currentAdvancedBody,
      currentBeginnerFooter,
      currentModerateFooter,
      currentAdvancedFooter,
    };
  };

  const validateBeforeSave = () => {
    const {
      currentBeginnerBody,
      currentModerateBody,
      currentAdvancedBody,
    } = readEditorValues();

    const missing = [];

    if (!title) {
      missing.push("Title is required");
    }
    if (!currentBeginnerBody) {
      missing.push("Beginner difficulty content is missing");
    }
    if (!currentModerateBody) {
      missing.push("Moderate difficulty content is missing");
    }
    if (!currentAdvancedBody) {
      missing.push("Advance difficulty content is missing");
    }

    if (missing.length > 0) {
      toast.error(missing.join(" | "));
      return false;
    }

    if (!mainCategoryId || !subCategoryId || !topicId) {
      toast.error("Missing category or topic information");
      return false;
    }

    return true;
  };

  const saveBlog = async ({ nextStatus }) => {
    if (!validateBeforeSave()) {
      return;
    }

    const {
      currentBeginnerBody,
      currentModerateBody,
      currentAdvancedBody,
      currentBeginnerFooter,
      currentModerateFooter,
      currentAdvancedFooter,
    } = readEditorValues();

    try {
      setIsSubmitting(true);
      setSubmitAction(nextStatus === "published" ? "publish" : "submit");
      const token = Cookies.get("adminToken");

      const postEntry = async (difficulty, body, footer) => {
        const formData = new FormData();
        if (entryId) {
          formData.append("entryId", entryId);
        }
        formData.append("title", title);
        formData.append("body", body);
        formData.append("difficulty", difficulty);
        formData.append("status", nextStatus);
        if (footer) {
          formData.append("footer", footer);
        }
        if (imageFile) {
          formData.append("image", imageFile);
        }

        return axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/admin/entry/${mainCategoryId}/${subCategoryId}/${topicId}`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          },
        );
      };

      const difficulties = [
        {
          difficulty: "Beginner",
          body: currentBeginnerBody,
          footer: currentBeginnerFooter,
        },
        {
          difficulty: "Moderate",
          body: currentModerateBody,
          footer: currentModerateFooter,
        },
        {
          difficulty: "Advance",
          body: currentAdvancedBody,
          footer: currentAdvancedFooter,
        },
      ];

      const responses = [];

      for (const item of difficulties) {
        const response = await postEntry(
          item.difficulty,
          item.body,
          item.footer,
        );
        responses.push(response);
      }

      const allSucceeded = responses.every(
        (response) => response.status === 201 || response.data?.success,
      );

      if (!allSucceeded) {
        toast.error("Failed to save blog");
        return;
      }

      const savedEntryId =
        responses[0]?.data?.entryId || responses[0]?.data?.data?.entryId || "";

      if (nextStatus === "published") {
        toast.success("Blog published successfully");
        setTimeout(() => {
          navigate(`/admin/category/${mainCategoryId}`);
        }, 800);
        return;
      }

      toast.success("Blog submitted successfully");
      setTimeout(() => {
        navigate(
          `/admin/topic-entries/${mainCategoryId}/${subCategoryId}/${topicId}/preview/${savedEntryId}`,
        );
      }, 800);
    } catch (error) {
      console.error("Add entry error", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to save blog",
      );
    } finally {
      setIsSubmitting(false);
      setSubmitAction("");
    }
  };

  const handleSubmitDraft = async () => {
    await saveBlog({ nextStatus: "draft" });
  };

  const handleDirectPublish = async () => {
    await saveBlog({ nextStatus: "published" });
  };

  const handlePreview = () => {
    if (!entryId) return;
    navigate(
      `/admin/topic-entries/${mainCategoryId}/${subCategoryId}/${topicId}/preview/${entryId}`,
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleBeginnerBodyInput = () => {
    if (!beginnerBodyEditorRef.current) {
      return;
    }
    const html = beginnerBodyEditorRef.current.innerHTML;
    setBeginnerBody(html);
  };

  const handleIntermediateBodyInput = () => {
    if (!moderateBodyEditorRef.current) {
      return;
    }
    const html = moderateBodyEditorRef.current.innerHTML;
    setModerateBody(html);
  };

  const handleAdvancedBodyInput = () => {
    if (!advancedBodyEditorRef.current) {
      return;
    }
    const html = advancedBodyEditorRef.current.innerHTML;
    setAdvancedBody(html);
  };

  const handleBeginnerFooterInput = () => {
    if (!beginnerFooterEditorRef.current) {
      return;
    }

    const html = beginnerFooterEditorRef.current.innerHTML;
    setBeginnerFooter(html);
  };

  const handleModerateFooterInput = () => {
    if (!moderateFooterEditorRef.current) {
      return;
    }
    const html = moderateFooterEditorRef.current.innerHTML;
    setModerateFooter(html);
  };

  const handleAdvancedFooterInput = () => {
    if (!advancedFooterEditorRef.current) {
      return;
    }
    const html = advancedFooterEditorRef.current.innerHTML;
    setAdvancedFooter(html);
  };

  useEffect(() => {
    const hydrate = async () => {
      try {
        if (!entryId || !mainCategoryId || !subCategoryId || !topicId) {
          return;
        }

        const token = Cookies.get("adminToken");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/admin/entries/${mainCategoryId}/${subCategoryId}/${topicId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        const data = response.data?.data;
        const list = data?.entries || [];

        const targetEntry = list.find((entry) => entry._id === entryId);
        const groupTitle = targetEntry ? targetEntry.title : null;
        const groupedEntries = groupTitle
          ? list.filter((entry) => entry.title === groupTitle)
          : [];

        const difficulties = ["Beginner", "Moderate", "Advance"];
        const combined = {
          Beginner: { body: null, footer: null },
          Moderate: { body: null, footer: null },
          Advance: { body: null, footer: null },
        };

        groupedEntries.forEach((entry) => {
          if (entry?.contentByDifficulty) {
            difficulties.forEach((d) => {
              const c = entry.contentByDifficulty?.[d];
              if (!combined[d].body && c?.body) combined[d].body = c.body;
              if (!combined[d].footer && c?.footer)
                combined[d].footer = c.footer;
            });
            return;
          }

          const d = entry?.difficulty;
          if (difficulties.includes(d)) {
            if (!combined[d].body && entry?.body) combined[d].body = entry.body;
            if (!combined[d].footer && entry?.footer)
              combined[d].footer = entry.footer;
          }
        });

        setTitle(groupTitle || "");
        setBeginnerBody(combined.Beginner.body || "");
        setModerateBody(combined.Moderate.body || "");
        setAdvancedBody(combined.Advance.body || "");
        setBeginnerFooter(combined.Beginner.footer || "");
        setModerateFooter(combined.Moderate.footer || "");
        setAdvancedFooter(combined.Advance.footer || "");

        requestAnimationFrame(() => {
          if (beginnerBodyEditorRef.current)
            beginnerBodyEditorRef.current.innerHTML = combined.Beginner.body || "";
          if (moderateBodyEditorRef.current)
            moderateBodyEditorRef.current.innerHTML = combined.Moderate.body || "";
          if (advancedBodyEditorRef.current)
            advancedBodyEditorRef.current.innerHTML = combined.Advance.body || "";
          if (beginnerFooterEditorRef.current)
            beginnerFooterEditorRef.current.innerHTML =
              combined.Beginner.footer || "";
          if (moderateFooterEditorRef.current)
            moderateFooterEditorRef.current.innerHTML =
              combined.Moderate.footer || "";
          if (advancedFooterEditorRef.current)
            advancedFooterEditorRef.current.innerHTML =
              combined.Advance.footer || "";
        });
      } catch (error) {
        console.error("entry hydrate error", error);
      }
    };

    hydrate();
  }, [entryId, mainCategoryId, subCategoryId, topicId]);

  const applyCommandToEditor = (editorRef, handleInput, command, value) => {
    if (!editorRef.current) {
      return;
    }
    editorRef.current.focus();
    if (
      typeof document !== "undefined" &&
      typeof document.execCommand === "function"
    ) {
      document.execCommand(command, false, value || null);
      if (handleInput) {
        handleInput();
      }
    }
  }
  const RichTextToolbar = ({ editorRef, onInput }) => {
    return (
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() => applyCommandToEditor(editorRef, onInput, "bold")}
        >
          Highlight
        </button>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() => applyCommandToEditor(editorRef, onInput, "italic")}
        >
          Italic
        </button>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() => applyCommandToEditor(editorRef, onInput, "underline")}
        >
          Underline
        </button>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() =>
            applyCommandToEditor(editorRef, onInput, "insertUnorderedList")
          }
        >
          • List
        </button>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() =>
            applyCommandToEditor(editorRef, onInput, "insertOrderedList")
          }
        >
          1. List
        </button>
        <button
          type="button"
          className={styles.toolbarButton}
          onClick={() => applyCommandToEditor(editorRef, onInput, "removeFormat")}
        >
          Clear
        </button>
      </div>
    );
  };

  const handleLogout = () => {
    Cookies.remove("adminToken");
    navigate("/admin/signin");
  };

  return (
    <>
      <Toast />
      <div className={styles.pageContainer}>
        {/* Simple Header */}
        <header className={styles.simpleHeader}>
          <div className={styles.headerTop}>
            <div className={styles.headerLeft}>
              <button
                className={styles.backBtn}
                onClick={() => navigate(`/admin/category/${mainCategoryId}`)}
                title="Back to Knowledge Center"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span>Back to Knowledge Center</span>
              </button>
            </div>
            <div className={styles.headerCenter}>
              <h1 className={styles.pageTitle}>
                {entryId ? "Edit Blog" : "Add Blog"}
              </h1>
              {topicName && (
                <p className={styles.topicSubtext}>
                  {entryId ? "Editing blog in" : "Adding new blog to"}{" "}
                  <strong>{topicName}</strong> topic
                </p>
              )}
            </div>
            <div className={styles.headerRight}>
              <button
                className={styles.logoutBtn}
                onClick={handleLogout}
                title="Logout"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className={styles.mainContentArea}>
          <div className={styles.formContainer}>
            <form encType="multipart/form-data" className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Title</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Entry title"
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className={styles.input}
                    onChange={handleImageChange}
                  />
                </div>

                <div className={styles.difficultySection}>
                  <div className={styles.difficultyHeader}>
                    <span className={styles.difficultyTitle}>Beginner</span>
                    <span className={styles.difficultyTag}>Difficulty</span>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Body</label>
                    <RichTextToolbar
                      editorRef={beginnerBodyEditorRef}
                      onInput={handleBeginnerBodyInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={beginnerBodyEditorRef}
                      onInput={handleBeginnerBodyInput}
                      style={{ minHeight: 120 }}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Footer (optional)</label>
                    <RichTextToolbar
                      editorRef={beginnerFooterEditorRef}
                      onInput={handleBeginnerFooterInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={beginnerFooterEditorRef}
                      onInput={handleBeginnerFooterInput}
                      style={{ minHeight: 80 }}
                    />
                  </div>
                </div>

                <div className={styles.difficultySection}>
                  <div className={styles.difficultyHeader}>
                    <span className={styles.difficultyTitle}>Moderate</span>
                    <span className={styles.difficultyTag}>Difficulty</span>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Body</label>
                    <RichTextToolbar
                      editorRef={moderateBodyEditorRef}
                      onInput={handleIntermediateBodyInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={moderateBodyEditorRef}
                      onInput={handleIntermediateBodyInput}
                      style={{ minHeight: 120 }}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Footer (optional)</label>
                    <RichTextToolbar
                      editorRef={moderateFooterEditorRef}
                      onInput={handleModerateFooterInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={moderateFooterEditorRef}
                      onInput={handleModerateFooterInput}
                      style={{ minHeight: 80 }}
                    />
                  </div>
                </div>
                <div className={styles.difficultySection}>
                  <div className={styles.difficultyHeader}>
                    <span className={styles.difficultyTitle}>Advance</span>
                    <span className={styles.difficultyTag}>Difficulty</span>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Body</label>
                    <RichTextToolbar
                      editorRef={advancedBodyEditorRef}
                      onInput={handleAdvancedBodyInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={advancedBodyEditorRef}
                      onInput={handleAdvancedBodyInput}
                      style={{ minHeight: 120 }}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Footer (optional)</label>
                    <RichTextToolbar
                      editorRef={advancedFooterEditorRef}
                      onInput={handleAdvancedFooterInput}
                    />
                    <div
                      className={styles.richInput}
                      contentEditable
                      suppressContentEditableWarning={true}
                      ref={advancedFooterEditorRef}
                      onInput={handleAdvancedFooterInput}
                      style={{ minHeight: 80 }}
                    />
                  </div>
                </div>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleSubmitDraft}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && submitAction === "submit"
                      ? "Submitting..."
                      : "Submit Blog"}
                  </button>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handlePreview}
                    disabled={isSubmitting || !entryId}
                  >
                    Preview Blog
                  </button>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={handleDirectPublish}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && submitAction === "publish"
                      ? "Publishing..."
                      : "Publish Blog"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  };
